import React, { useEffect, useState } from 'react';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import { useParams } from 'react-router';
import { Button, Col, Collapse, Divider, Form, Input, Row, Select, Space } from 'antd';
import { ArrowRightOutlined, ScanOutlined } from '@ant-design/icons';
import { saveData, fetchData } from '../../service/dataServices';
import moment from 'moment';
import { getFormItems } from '../../components/form/formComponents';
import { getOperationType, rowStyle } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import LinkDrawer from './LinkDrawer';
import ObjectChangeHistory from './ObjectChangeHistory';
import QrCodeModal from './QrCodeModal';
import UserDropdown from '../../components/form/userDropdown';
import useDropdownFormItemHook from '../../components/form/DropdownFormItem';
import { useTranslation } from 'react-i18next';
import { getLocalizedName, getLocalizedTitle, localizeField } from '../../utils/localization';
import GenericFormPdf from '../../../components/Pdf/GenericFormPdf';
import GlbUploader from '../../components/image-upload/GlbUploader';
import MediaUploader from '../../components/image-upload/MediaUploader';

const { Option } = Select;

const SaveObject = () => {
  const { t } = useTranslation();
  const PATH = '/api/objects';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission("item");

  const [form] = Form.useForm();
  const [initialOtherDimensions, setInitialOtherDimensions] = useState([]);
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [glbFileList, setGlbFileList] = useState([]);
  const [videoFileList, setVideoFileList] = useState([]);
  const [audioFileList, setAudioFileList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [availability, setAvailability] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentInventoryNumber, setCurrentInventoryNumber] = useState('');
  const [currentObjectTitle, setCurrentObjectTitle] = useState({});
  const [currentDescription, setCurrentDescription] = useState({});
  const [refetchChangeHistory, setRefetchChangeHistory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCatSettings, setSelectedCatSettings] = useState(null);
  const [currentCountry, setCurrentCountry] = useState({});    
  const [currentCentury, setCurrentCentury] = useState({});

  // Address state
  const [addressData, setAddressData] = useState([]);
  const [selectedHall, setSelectedHall] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [users, setUsers] = useState([]);
  const [exportButtonClicked, setExportButtonClicked] = useState(false);

  fetchData('/api/settings/categories/' + selectedCategoryId, resp => setSelectedCatSettings(resp), null, selectedCategoryId !== null)

  fetchData('/api/dropdowns', setDropdowns);
  fetchData('/api/addresses', setAddressData);
  fetchData('/api/users/user-refs', setUsers);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }
  const saveMutation = saveData(PATH, operation.method);

  useEffect(() => {
    if (initialData && dropdowns) {
      setInitialOtherDimensions(initialData.otherDimensions || []);
      if (initialData.mainImage) setMainImageFileList([initialData.mainImage]);
      if (initialData.imageList) setImageList(initialData.imageList);
      if (initialData.glbFiles) setGlbFileList(initialData.glbFiles);
      if (initialData.videoFiles) setVideoFileList(initialData.videoFiles);
      if (initialData.audioFiles) setAudioFileList(initialData.audioFiles);
      if (initialData.dropdowns && initialData.dropdowns.classification && dropdowns.classification) {
        const found = dropdowns.classification.find(dd => dd.id === initialData.dropdowns.classification);
        if (found && found.children) setCategoryList(found.children);
      }

    const processedData = { ...initialData };
    if (typeof processedData.country === 'string') {
      processedData.country = { en: processedData.country, ar: '' };
    }
    if (typeof processedData.century === 'string') {
      processedData.century = { en: processedData.century, ar: '' };
    }
      form.setFieldsValue({
        ...processedData,
        registrationDate: initialData.registrationDate ? moment(initialData.registrationDate) : null,
        date: initialData.date ? moment(initialData.date) : null,
      });
    }
  }, [initialData, dropdowns]);

  // Initialize address cascading selections from initial data
  useEffect(() => {
    if (initialData && addressData.length > 0) {
      if (initialData.hallId) {
        const hall = addressData.find(h => h.id === initialData.hallId);
        if (hall) {
          setSelectedHall(hall);
          if (initialData.zoneId && hall.children) {
            const zone = hall.children.find(z => z.id === initialData.zoneId);
            if (zone) {
              setSelectedZone(zone);
            }
          }
        }
      }
    }
  }, [initialData, addressData]);

  useEffect(() => {
    if (selectedCatSettings?.currentNumber && !id) {
      // Convert to string and pad with '0' until it reaches 5 characters
      const paddedNumber = String(selectedCatSettings.currentNumber).padStart(5, '0');
      const inventoryNumber = `${selectedCatSettings?.prefix}${paddedNumber}`;
      // const inventoryNumber = `${selectedCatSettings?.prefix}${selectedCatSettings?.currentNumber}`
      form.setFieldsValue({ inventoryNumber: inventoryNumber })
    }
  }, [selectedCatSettings])

  const triggerChangeHistoryRefetch = () => {
    setRefetchChangeHistory(new Date());
  }

  const onFinish = (values) => {
    const dataToSend = {
      ...values,
      mainImage: mainImageFileList ? mainImageFileList[0] : undefined,
      imageList: imageList || undefined,
      glbFiles: glbFileList || undefined,
      videoFiles: videoFileList || undefined,
      audioFiles: audioFileList || undefined,
    };
    if (id) {
      saveMutation.mutate({ dataToSend, id }, { onSuccess: triggerChangeHistoryRefetch });
    } else {
      saveMutation.mutate({ dataToSend }, { onSuccess: triggerChangeHistoryRefetch });
    }
  };

  const onAvailability = (values) => {
    form.setFieldsValue({ available: values });
    setAvailability(values);
    if (values === true) {
      form.setFieldsValue({ dropdowns: { availabilityPurpose: undefined } })
    }
  };

  const onCategorySelect = (val) => {
    setSelectedCategoryId(val);
  };

  const openQrModal = () => {
    const invNum = form.getFieldValue('inventoryNumber');
    if (invNum) {
      setCurrentInventoryNumber(invNum);
      setCurrentObjectTitle(form.getFieldValue('objectTitle'));
      setCurrentDescription(form.getFieldValue('description'));
      setCurrentCountry(form.getFieldValue('country'));  
      setCurrentCentury(form.getFieldValue('century'));    
      setQrModalOpen(true);
    }
  };

  const {
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getImageListFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getTranslatableFormItem,
    getSwitchFormItem,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
    getParentSelectFormItem,
    getChildSelectFormItem,
  } = getFormItems(dropdowns, hasWritePerm);

  const { singleSelectFormItem, multiSelectFormItem } = useDropdownFormItemHook(hasWritePerm)

  const resolveDropdown = (type, value) => {
    const options = dropdowns && dropdowns[type];
    const found = options && options.find(option => option.id === value);
    return found ? getLocalizedTitle(found) : '';
  };
  const resolveMultiselect = (type, values) => (values || [])
    .map(value => resolveDropdown(type, value))
    .filter(Boolean)
    .join(', ');

  const buildPdfReport = () => {
    const values = form.getFieldsValue();
    const d = values.dropdowns || {};
    const m = values.multiSelects || {};
    const otherDimensions = Object.entries(values.otherDimensions || {})
      .map(([dimensionId, value]) => `${resolveDropdown('otherDimensions', parseInt(dimensionId, 10))}: ${value}`)
      .join(', ');
    const mainImageUrl = mainImageFileList && mainImageFileList[0] ? mainImageFileList[0].url : null;
    const displayStation = selectedZone?.children?.find(s => s.id === values.displayStationId);
    const recordedByUser = users.find(u => u.id === values.userId);

    return {
      title: t('objects.subTitle'),
      subtitle: values.inventoryNumber ? `${t('objects.inventoryNumber')}: ${values.inventoryNumber}` : '',
      fileName: `object_${values.inventoryNumber || 'draft'}_(${new Date().toISOString().slice(0, 10)}).pdf`,
      sections: [
        {
          title: t('objects.classification'),
          rows: [{
            columns: [
              { weight: 1, fields: [{ label: t('objects.classification'), value: resolveDropdown('classification', d.classification) }] },
              { weight: 1, fields: [{ label: t('objects.category'), value: resolveDropdown('category', d.category) }] },
            ],
          }],
        },
        {
          title: t('objects.subTitle'),
          rows: [{
            columns: [
              { weight: 1, fields: [{ label: t('objects.mainImage', 'Main Image'), type: 'image', value: mainImageUrl }] },
              {
                weight: 1,
                fields: [
                  { label: t('objects.inventoryNumber'), value: values.inventoryNumber },
                  { label: t('objects.quantity'), value: values.quantity },
                  { label: t('objects.objectName'), value: values.objectName },
                  { label: t('objects.objectTitle'), value: localizeField(values.objectTitle) },
                  { label: t('objects.author'), value: values.author },
                  { label: t('objects.availability'), value: values.available ? t('common.yes', 'Yes') : t('common.no', 'No') },
                  { label: t('objects.availabilityPurpose'), value: resolveDropdown('availabilityPurpose', d.availabilityPurpose) },
                ],
              },
              {
                weight: 1,
                fields: [
                  { label: t('objects.material'), value: resolveMultiselect('material', m.material) },
                  { label: t('objects.technique'), value: resolveMultiselect('technique', m.technique) },
                  { label: t('objects.region'), value: resolveDropdown('region', d.region) },
                  { label: t('objects.country'), value: localizeField(values.country) },
                  { label: t('objects.city'), value: values.city },
                  { label: t('objects.century'), value: localizeField(values.century) },
                  { label: t('objects.date'), value: values.date ? moment(values.date).format('YYYY-MM-DD') : '' },
                ],
              },
              {
                weight: 1,
                fields: [
                  { label: t('objects.length'), value: values.length },
                  { label: t('objects.width'), value: values.width },
                  { label: t('objects.height'), value: values.height },
                  { label: t('objects.weight'), value: values.weight },
                  { label: t('objects.otherDimensions', 'Other Dimensions'), value: otherDimensions },
                ],
              },
            ],
          }],
        },
        {
          title: t('objects.description'),
          rows: [{
            columns: [{
              weight: 1,
              fields: [
                { label: t('objects.description'), value: localizeField(values.description) },
                { label: t('objects.inscriptionType'), value: values.inscriptionType },
                { label: t('objects.inscriptionPosition'), value: values.inscriptionPosition },
              ],
            }],
          }],
        },
        {
          title: t('objects.location'),
          rows: [
            {
              columns: [
                { weight: 1, fields: [{ label: t('objects.location'), value: resolveDropdown('location', d.location) }] },
                { weight: 1, fields: [{ label: t('objects.locationArea'), value: resolveDropdown('locationArea', d.locationArea) }] },
                { weight: 1, fields: [{ label: t('objects.displayArea'), value: resolveDropdown('displayArea', d.displayArea) }] },
              ],
            },
            {
              columns: [
                { weight: 1, fields: [{ label: t('objects.showcaseNumber'), value: values.showcaseNumber }] },
                { weight: 1, fields: [{ label: t('objects.shelfNumber'), value: values.shelfNumber }] },
                { weight: 1, fields: [{ label: t('objects.qrCodeRef'), value: values.qrCodeRef }] },
              ],
            },
            {
              columns: [
                { weight: 1, fields: [{ label: t('objects.hall'), value: selectedHall ? getLocalizedName(selectedHall) : '' }] },
                { weight: 1, fields: [{ label: t('objects.zone'), value: selectedZone ? getLocalizedName(selectedZone) : '' }] },
                { weight: 1, fields: [{ label: t('objects.displayStation'), value: displayStation ? getLocalizedName(displayStation) : '' }] },
              ],
            },
          ],
        },
        {
        title: t('objects.photos', 'Photos'),
          rows: [{
            columns: [{ weight: 1, fields: [{ label: t('objects.photos', 'Photos'), type: 'gallery', value: (imageList || []).map(i => i.url).filter(Boolean) }] }],
          }],
        },
        {
          title: t('objects.glbModel', '3D Model (GLB)'),
          rows: [{
            columns: [{ weight: 1, fields: [{ label: t('objects.glbModel', '3D Model (GLB)'), value: glbFileList && glbFileList[0] ? glbFileList[0].name : '' }] }],
          }],
        },
        {
          title: t('objects.recordedBy'),
          rows: [{
            columns: [
              { weight: 1, fields: [{ label: t('objects.registrationDate'), value: values.registrationDate ? moment(values.registrationDate).format('YYYY-MM-DD') : '' }] },
              { weight: 1, fields: [{ label: t('objects.recordedBy'), value: values.recordedBy }] },
              { weight: 1, fields: [{ label: t('objects.recordedBy'), value: recordedByUser ? recordedByUser.name : '' }] },
            ],
          }],
        },
      ],
    };
  };

  const handleExport = () => setExportButtonClicked(true);
  const exportCallback = () => setExportButtonClicked(false);

  return (
    <CustomAddNewPageContent
      title={t(`objects.${operation.type === 'Add' ? 'addObject' : 'saveObject'}`)}
      subTitle={t('objects.subTitle')}
      exportButton={
        <Space>
          <Button type="primary" onClick={handleExport}>
            {t('objects.exportPdf', 'Export PDF')}
          </Button>
          <Button
            icon={<ScanOutlined />}
            onClick={openQrModal}
          >
            {t('objects.qrCode')}
          </Button>
          {id && (
            <Button icon={<ArrowRightOutlined />} onClick={() => setDrawerOpen(true)}>
              {t('objects.viewLinks')}
            </Button>
          )}
        </Space>
      }
    >
      <QrCodeModal
        open={qrModalOpen}
        setOpen={setQrModalOpen}
        inventoryNumber={currentInventoryNumber}
        objectTitle={currentObjectTitle}
        description={currentDescription}
        country={currentCountry}          
        century={currentCentury} 
      />
      {id && <LinkDrawer open={drawerOpen} setOpen={setDrawerOpen} objectId={id} />}
      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={12}>
            {getParentSelectFormItem('classification', t('objects.classification'), setCategoryList)}
          </Col>
          <Col span={12}>
            {getChildSelectFormItem('category', t('objects.category'), categoryList, false, onCategorySelect)}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {getMainImageFormItem('mainImage', false, 'objects', mainImageFileList, setMainImageFileList)}
          </Col>
          <Col span={16}>
            <Row>
              <Col span={12}>
                {getInputFormItem('inventoryNumber', t('objects.inventoryNumber'), true, 'text', !!id)}
              </Col>
              <Col span={12}>
                {getInputFormItem('quantity', t('objects.quantity'), false, 'number')}
              </Col>
              <Col span={12}>
                {getInputFormItem('objectName', t('objects.objectName'))}
              </Col>
              <Col span={12}>
                {getTranslatableFormItem('objectTitle', t('objects.objectTitle'))}
              </Col>
              <Col span={12}>
                {getInputFormItem('author', t('objects.author'))}
              </Col>

              <Col span={12}>
                {getSwitchFormItem('available', t('objects.availability'), onAvailability)}
              </Col>

              <Col span={12} />
              <Col span={12}>
                {singleSelectFormItem('availabilityPurpose', t('objects.availabilityPurpose'), availability)}
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={8}>
            {getInputNumberFormItem('length', t('objects.length'), 'cm')}
            {getInputNumberFormItem('width', t('objects.width'), 'cm')}
            {getInputNumberFormItem('height', t('objects.height'), 'cm')}
            {getInputNumberFormItem('weight', t('objects.weight'), 'gr')}
            {getOtherDimensionsFormItems(initialOtherDimensions)}
          </Col>
          <Col span={16}>
            <Row>

              <Col span={12}>
                {multiSelectFormItem('material', t('objects.material'))}
              </Col>
              <Col span={12}>
                {multiSelectFormItem('technique', t('objects.technique'))}
              </Col>
              <Col span={12}>
                {singleSelectFormItem('region', t('objects.region'))}
              </Col>
              <Col span={12}>
                {getTranslatableFormItem(
                  'country',
                  t('objects.country'),
                  false,          
                  undefined,      
                  false,          
                  undefined,     
                  { en: 'Enter country', ar: 'أدخل الدولة' }
                )}
              </Col>
              <Col span={12}>
                {getInputFormItem('city', t('objects.city'))}
              </Col>
              <Col span={12}>
                {getTranslatableFormItem(
                  'century',
                  t('objects.century'),
                  false,
                  undefined,
                  false,
                  undefined,
                  { en: 'Enter century', ar: 'أدخل القرن' }
                )}
              </Col>
              <Col span={12}>
                {getDatePickerFormItem('date', t('objects.date'))}
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={24}>
            {getTranslatableFormItem('description', t('objects.description'), true, 500, false, { span: 22 })}
          </Col>
          <Col span={12}>
            {getInputFormItem('inscriptionType', t('objects.inscriptionType'))}
          </Col>
          <Col span={12}>
            {getInputFormItem('inscriptionPosition', t('objects.inscriptionPosition'))}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={8}>
            {singleSelectFormItem('location', t('objects.location'))}
          </Col>
          <Col span={8}>
            {singleSelectFormItem('locationArea', t('objects.locationArea'))}
          </Col>
          <Col span={8}>
            {singleSelectFormItem('displayArea', t('objects.displayArea'))}
          </Col>
          <Col span={8}>
            {getInputFormItem('showcaseNumber', t('objects.showcaseNumber'))}
          </Col>
          <Col span={8}>
            {getInputFormItem('shelfNumber', t('objects.shelfNumber'))}
          </Col>
          <Col span={8}>
            {getInputFormItem('qrCodeRef', t('objects.qrCodeRef'))}
          </Col>
          <Col span={8}>
            <Form.Item label={t('objects.epcCode')} name="epcCode">
              <Input
                disabled={!hasWritePerm}
                placeholder={t('objects.epcCodePlaceholder')}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={8}>
            <Form.Item label={t('objects.hall')} name="hallId">
              <Select
                showSearch
                filterOption
                optionFilterProp="children"
                placeholder={`${t('common.select')} ${t('objects.hall')}`}
                allowClear
                onChange={(value) => {
                  if (value) {
                    const hall = addressData.find(h => h.id === value);
                    setSelectedHall(hall || null);
                  } else {
                    setSelectedHall(null);
                  }
                  setSelectedZone(null);
                  form.setFieldsValue({ zoneId: undefined, displayStationId: undefined });
                }}
              >
                {addressData.map(hall => (
                  <Option value={hall.id} key={hall.id}>{getLocalizedName(hall)}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('objects.zone')} name="zoneId">
              <Select
                showSearch
                filterOption
                optionFilterProp="children"
                placeholder={`${t('common.select')} ${t('objects.zone')}`}
                allowClear
                disabled={!selectedHall}
                onChange={(value) => {
                  if (value && selectedHall) {
                    const zone = selectedHall.children?.find(z => z.id === value);
                    setSelectedZone(zone || null);
                  } else {
                    setSelectedZone(null);
                  }
                  form.setFieldsValue({ displayStationId: undefined });
                }}
              >
                {selectedHall?.children?.map(zone => (
                  <Option value={zone.id} key={zone.id}>{getLocalizedName(zone)}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('objects.displayStation')} name="displayStationId">
              <Select
                showSearch
                filterOption
                optionFilterProp="children"
                placeholder={`${t('common.select')} ${t('objects.displayStation')}`}
                allowClear
                disabled={!selectedZone}
              >
                {selectedZone?.children?.map(station => (
                  <Option value={station.id} key={station.id}>{getLocalizedName(station)}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          {getImageListFormItem('imageList', false, 'objects', imageList, setImageList)}
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={12}>
            <Form.Item label={t('objects.glbModel', '3D Model (GLB)')}>
              <GlbUploader
                disabledAdd={!hasWritePerm}
                storageFolder="objects"
                fileList={glbFileList}
                setFileList={setGlbFileList}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={12}>
            <Form.Item label={t('objects.video', 'Video')}>
              <MediaUploader
                kind="video"
                disabledAdd={!hasWritePerm}
                storageFolder="objects"
                fileList={videoFileList}
                setFileList={setVideoFileList}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('objects.audio', 'Audio')}>
              <MediaUploader
                kind="audio"
                disabledAdd={!hasWritePerm}
                storageFolder="objects"
                fileList={audioFileList}
                setFileList={setAudioFileList}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={12}>
            {getDatePickerFormItem('registrationDate', t('objects.registrationDate'))}
          </Col>
          <Col span={12}>
            {getInputFormItem('recordedBy', t('objects.recordedBy'))}
          </Col>
        </Row>
        <Row style={rowStyle}>
          <Col span={12}>
            <UserDropdown label={t('objects.recordedBy')} fieldName='userId' hasWritePermission={hasWritePerm} />
          </Col>
        </Row>
        <Divider />
        {getFormSubmitButton()}
      </Form>

      <Divider />
      {id && <Collapse
        bordered={false}
        items={[
          {
            key: 'change-list',
            label: t('objects.changeHistory'),
            forceRender: true,
            children: (
              <ObjectChangeHistory
                entityId={id}
                refetchTrigger={refetchChangeHistory}
              />
            ),
          }]}
      />}
      {exportButtonClicked && (
        <GenericFormPdf {...buildPdfReport()} callback={exportCallback} />
      )}
    </CustomAddNewPageContent>
  );
};

export default SaveObject;
